import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../axiosInstance";

export const getCategories = createAsyncThunk(
  "category/getCategories",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/categories`);
      return data.categories;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
);

export const getCategory = createAsyncThunk(
  "category/getCategory",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/categories/${id}`);
      return data.category;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
);

export const createCategory = createAsyncThunk(
  "category/createCategory",
  async (categoryData, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`/categories`, categoryData);
      return data.category;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
);

export const updateCategory = createAsyncThunk(
  "category/updateCategory",
  async ({ id, categoryData }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`/categories/${id}`, categoryData);
      return data.category;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
);

export const deleteCategory = createAsyncThunk(
  "category/deleteCategory",
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/categories/${id}`);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
);

/* ------------------------------------------------------------------ */
/* Async Thunks — SubCategories                                       */
/* ------------------------------------------------------------------ */

export const getSubcategories = createAsyncThunk(
  "category/getSubcategories",
  async (categoryId, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/categories/${categoryId}/subcategories`);
      return { categoryId, subcategories: data.subcategories };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
);

export const createSubcategory = createAsyncThunk(
  "category/createSubcategory",
  async ({ categoryId, subData }, { rejectWithValue }) => {
    try {
      const { data } = await api.post(
        `/categories/${categoryId}/subcategories`,
        subData,
      );
      return data.category; // returns parent category with updated subcategories
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
);

export const updateSubcategory = createAsyncThunk(
  "category/updateSubcategory",
  async ({ categoryId, subId, subData }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(
        `/categories/${categoryId}/subcategories/${subId}`,
        subData,
      );
      return data.category;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
);

export const deleteSubcategory = createAsyncThunk(
  "category/deleteSubcategory",
  async ({ categoryId, subId }, { rejectWithValue }) => {
    try {
      await api.delete(`/categories/${categoryId}/subcategories/${subId}`);
      return { categoryId, subId };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
);

export const getAllSubcategories = createAsyncThunk(
  "category/getAllSubcategories",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/categories/subcategories/all`);
      return data.subcategories;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
);
/* ------------------------------------------------------------------ */
/* Slice                                                              */
/* ------------------------------------------------------------------ */

const initialState = {
  categories: [],
  category: null, // single selected category
  subcategories: [], // subcategories of selected category
  allSubcategories: [], // flattened list across all categories
  loading: false,
  error: null,
  success: false,
};

const categorySlice = createSlice({
  name: "category",
  initialState,
  reducers: {
    clearCategoryError: (state) => {
      state.error = null;
    },
    clearCategorySuccess: (state) => {
      state.success = false;
    },
    resetCategoryState: () => initialState,
    setSelectedCategory: (state, action) => {
      state.category = action.payload;
      state.subcategories = action.payload?.subcategories || [];
    },
  },
  extraReducers: (builder) => {
    builder
      /* ---------- Get All Categories ---------- */
      .addCase(getCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload;
      })
      .addCase(getCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ---------- Get Single Category ---------- */
      .addCase(getCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.category = action.payload;
        state.subcategories = action.payload.subcategories || [];
      })
      .addCase(getCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ---------- Create Category ---------- */
      .addCase(createCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.categories.unshift(action.payload);
      })
      .addCase(createCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ---------- Update Category ---------- */
      .addCase(updateCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        const idx = state.categories.findIndex(
          (c) => c._id === action.payload._id,
        );
        if (idx !== -1) state.categories[idx] = action.payload;
        if (state.category?._id === action.payload._id) {
          state.category = action.payload;
          state.subcategories = action.payload.subcategories || [];
        }
      })
      .addCase(updateCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ---------- Delete Category ---------- */
      .addCase(deleteCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.categories = state.categories.filter(
          (c) => c._id !== action.payload,
        );
        if (state.category?._id === action.payload) {
          state.category = null;
          state.subcategories = [];
        }
      })
      .addCase(deleteCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ---------- Get Subcategories ---------- */
      .addCase(getSubcategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getSubcategories.fulfilled, (state, action) => {
        state.loading = false;
        state.subcategories = action.payload.subcategories;
      })
      .addCase(getSubcategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ---------- Create Subcategory ---------- */
      .addCase(createSubcategory.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createSubcategory.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        const updated = action.payload;
        const idx = state.categories.findIndex((c) => c._id === updated._id);
        if (idx !== -1) state.categories[idx] = updated;
        if (state.category?._id === updated._id) {
          state.category = updated;
          state.subcategories = updated.subcategories || [];
        }
      })
      .addCase(createSubcategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ---------- Update Subcategory ---------- */
      .addCase(updateSubcategory.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateSubcategory.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        const updated = action.payload;
        const idx = state.categories.findIndex((c) => c._id === updated._id);
        if (idx !== -1) state.categories[idx] = updated;
        if (state.category?._id === updated._id) {
          state.category = updated;
          state.subcategories = updated.subcategories || [];
        }
      })
      .addCase(updateSubcategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ---------- Delete Subcategory ---------- */
      .addCase(deleteSubcategory.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteSubcategory.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        const { categoryId, subId } = action.payload;
        state.subcategories = state.subcategories.filter(
          (s) => s._id !== subId,
        );

        const catIdx = state.categories.findIndex((c) => c._id === categoryId);
        if (catIdx !== -1) {
          state.categories[catIdx].subcategories = state.categories[
            catIdx
          ].subcategories.filter((s) => s._id !== subId);
        }
        if (state.category?._id === categoryId) {
          state.category.subcategories = state.category.subcategories.filter(
            (s) => s._id !== subId,
          );
        }
      })
      .addCase(deleteSubcategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ---------- Get All Subcategories (Flattened) ---------- */
      .addCase(getAllSubcategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllSubcategories.fulfilled, (state, action) => {
        state.loading = false;
        state.allSubcategories = action.payload;
      })
      .addCase(getAllSubcategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearCategoryError,
  clearCategorySuccess,
  resetCategoryState,
  setSelectedCategory,
} = categorySlice.actions;

export default categorySlice.reducer;
